import React, {useContext} from "react";
import {Image} from "antd";
import dayjs from "dayjs";
import useTranslate from "../../../../../modules/local/useTranslate";
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";
import {ImagePlaceholder} from "../../../../../conts/imageBase64";
import {ifThenElseMultipleConditions} from "../../../../../helpers/ifThenElseMultipleConditions";
import {formatSimplifyDate, isSameDay} from "../../../../../helpers/dateUtils";
import {BsMegaphoneFill} from "react-icons/bs";

export const CourseClassAnnouncementItem = ({item}) => {

    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const {
        title, type_label, type_code, photo, is_for_all,
        content, group,
        time_close, time_open, shared_url,
    } = item ?? {}

    const TagBlock = ({overlay = false}) => type_label && (
        <div
            className="p-3 flex items-center gap-2"
            style={overlay ? {
                background: 'rgba(255,255,255, 0.92)',
                backdropFilter: 'blur(6px)',
                borderRadius: 12,
                padding: '6px 10px 6px 6px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.13)',
            } : {}}>
            {/* Icon circle */}
            <div
                style={{
                    width: 28,
                    height: 28,
                    borderRadius: 12,
                    flexShrink: 0,
                }}
                className="flex flex-center bg-blue-500">
                <BsMegaphoneFill size={15} className="text-white"/>
            </div>
            <span className="flex-1 text-sm text-blue-600 tracking-wider leading-snug">
                {/*{t('notice of')}*/}
                <span className="ml-1 font-semibold italic">
                    {type_label}
                </span>
            </span>
        </div>
    )

    return (
        <div className="flex flex-col border border-color-50 background shadow-md rounded-xl relative">
            {photo ? (
                <div className="relative">
                    <Image
                        src={photo}
                        fallback={ImagePlaceholder}
                        style={{maxHeight: 420}}
                        wrapperClassName="w-full mx-auto rounded-t-xl"
                        className="object-cover w-full rounded-t-xl"
                        preview={false}
                    />
                    {type_label &&  (
                        <div className="absolute top-10 left-10">
                            <TagBlock overlay/>
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-center gap-2">
                    <TagBlock />
                </div>
            )}
            <div className="p-3 flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <div className="font-bold text-lg text-gray-800 tracking-wide">
                        {title}
                    </div>
                    <div className="text-gray-700 text-base whitespace-pre-line">
                        {content}
                    </div>
                    {shared_url && (
                        <a
                            href={shared_url} target="_blank" rel="noopener noreferrer"
                            className="flex-1 text-blue-600 italic no-underline max-lines-1">
                            {shared_url}
                        </a>
                    )}
                </div>
                {ifThenElseMultipleConditions(
                    null,
                    time_open && time_close && isSameDay(time_open, time_close) === true,
                    <div className="p-1 pr-3 w-full mx-auto flex items-center gap-2 background rounded-xl">
                        <div
                            className={`w-15 h-15 p-1 flex flex-col flex-center text-white shadow bg-secondary rounded-xl scale-90`}>
                            <span className="text-xs tracking-wide capitalize">
                                {dayjs(time_open || time_close).format('MMMM')}
                            </span>
                            <span className="font-bold text-lg leading-tight">
                                {dayjs(time_open || time_close).format('DD')}
                            </span>
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-xs text-color-400 tracking-wider">
                                {t('started at')}
                            </span>
                            <span className={`font-semibold text-sm tracking-wide capitalize text-color-000`}>
                                {formatSimplifyDate(time_open, '', 'HH:mm')} - {formatSimplifyDate(time_close, '', 'HH:mm')}
                                {' | '}
                                {formatSimplifyDate(time_open || time_close, 'fullShort')}
                            </span>
                        </div>
                    </div>,
                    time_open && time_close,
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="col-span-1 p-1 flex items-center gap-2 background rounded-xl">
                            <div
                                className={`w-15 h-15 p-1 flex flex-col flex-center text-white shadow bg-secondary rounded-xl scale-90`}>
                                <span className="text-xs tracking-wide capitalize">
                                    {dayjs(time_open).format('MMMM')}
                                </span>
                                <span className="font-bold text-lg leading-tight">
                                    {dayjs(time_open).format('DD')}
                                </span>
                            </div>
                            <div className="flex flex-col flex-1">
                                <span className="text-xs text-color-400 tracking-wider">
                                    {t('started at')}
                                </span>
                                <span className={`font-semibold text-sm tracking-wide capitalize text-color-000`}>
                                    {formatSimplifyDate(time_open, 'fullTime')}
                                </span>
                            </div>
                        </div>
                        <div className="col-span-1 p-1 flex items-center gap-2 background rounded-xl">
                            <div
                                className={`w-15 h-15 p-1 flex flex-col flex-center text-white shadow bg-secondary rounded-xl scale-90`}>
                                <span className="text-xs tracking-wide capitalize">
                                    {dayjs(time_close).format('MMMM')}
                                </span>
                                <span className="font-bold text-lg leading-tight">
                                    {dayjs(time_close).format('DD')}
                                </span>
                            </div>
                            <div className="flex flex-col flex-1">
                                <span className="text-xs text-color-400 tracking-wider">
                                    {t('finished at')}
                                </span>
                                <span className={`font-semibold text-sm tracking-wide capitalize text-color-000`}>
                                    {formatSimplifyDate(time_close, 'fullTime')}
                                </span>
                            </div>
                        </div>
                    </div>,
                    time_open,
                    <div className="p-1 pr-3 w-full mx-auto flex items-center gap-2 background rounded-xl">
                        <div
                            className={`w-15 h-15 p-1 flex flex-col flex-center text-white shadow bg-secondary rounded-xl scale-90`}>
                            <span className="text-xs tracking-wide capitalize">
                                {dayjs(time_open).format('MMMM')}
                            </span>
                            <span className="font-bold text-lg leading-tight">
                                {dayjs(time_open).format('DD')}
                            </span>
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-xs text-color-400 tracking-wider">
                                {t('started at')}
                            </span>
                            <span className={`font-semibold text-sm tracking-wide capitalize text-color-000`}>
                                {formatSimplifyDate(time_open, 'fullTime')}
                            </span>
                        </div>
                    </div>,
                    time_close,
                    <div className="p-1 pr-3 w-full mx-auto flex items-center gap-2 background rounded-xl">
                        <div
                            className={`w-15 h-15 p-1 flex flex-col flex-center text-white shadow bg-secondary rounded-xl scale-90`}>
                            <span className="text-xs tracking-wide capitalize">
                                {dayjs(time_close).format('MMMM')}
                            </span>
                            <span className="font-bold text-lg leading-tight">
                                {dayjs(time_close).format('DD')}
                            </span>
                        </div>
                        <div className="flex flex-col flex-1">
                            <span className="text-xs text-color-400 tracking-wider">
                                {t('finished at')}
                            </span>
                            <span className={`font-semibold text-sm tracking-wide capitalize text-color-000`}>
                                {formatSimplifyDate( time_close, 'fullTime')}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )

}